<template>
  <div class="drupal-autocomplete relative" ref="wrapperRef">
    <slot
      :on-input="onInput"
      :on-keydown="onKeydown"
      :on-focus="onFocus"
      :on-blur="onBlur"
    />
    <!-- <Teleport to="body"> -->
    <ul
      v-if="showDropdown"
      class="m-0 p-2 rounded list-none bg-white shadow overflow-x-hidden overflow-y-auto max-h-[200px]"
      :style="dropdownStyle"
    >
      <li v-if="isLoading" class="flex align-middle justify-center p-2">
        <i class="pi pi-spin pi-spinner" />
      </li>
      <li
        v-else
        v-for="(item, index) in suggestions"
        :key="index"
        class="cursor-pointer transition-colors ease-in-out duration-200 text-surface-500"
        :class="[
          {
            'bg-mbzuai-sand-200 text-mbzuai-dark-navy-500! rounded-sm':
              index === activeIndex,
          },
        ]"
        @mousedown.prevent="onSelect(item)"
        @mouseenter="activeIndex = index"
      >
        <span
          class="block py-2 px-3 whitespace-nowrap overflow-hidden text-ellipsis"
          >{{ item.value }}</span
        >
      </li>
    </ul>
    <!-- </Teleport> -->
  </div>
</template>

<script setup lang="ts">
interface Suggestion {
  value: string;
  url?: string;
}

const props = withDefaults(
  defineProps<{
    searchId: string;
    modelValue?: string;
    disabled?: boolean;
  }>(),
  {
    modelValue: '',
    disabled: false,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  submit: [];
}>();

const { suggestions, isLoading, fetchSuggestions, clear } =
  useDrupalAutocomplete(props.searchId);

const activeIndex = ref(-1);
const isFocused = ref(false);
const wrapperRef = ref<HTMLElement | null>(null);

const dropdownStyle = ref<Record<string, string>>({});

const showDropdown = computed(() => {
  return isFocused.value && (suggestions.value.length > 0 || isLoading.value);
});

function updateDropdownPosition() {
  if (!wrapperRef.value) return;
  const rect = wrapperRef.value.getBoundingClientRect();
  dropdownStyle.value = {
    position: 'absolute',
    top: `50px`,
    left: `0px`,
    width: `${rect.width}px`,
    zIndex: '99999',
  };
}

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  emit('update:modelValue', value);
  activeIndex.value = -1;
  if (!props.disabled) fetchSuggestions(value);
  updateDropdownPosition();
}

function onFocus() {
  isFocused.value = true;
  updateDropdownPosition();
  if (!props.disabled && props.modelValue && props.modelValue.trim().length >= 3) {
    fetchSuggestions(props.modelValue);
  }
}

function onBlur() {
  setTimeout(() => {
    isFocused.value = false;
    activeIndex.value = -1;
  }, 200);
}

function onKeydown(event: KeyboardEvent) {
  if (!showDropdown.value) {
    if (event.key === 'Enter') {
      emit('submit');
    }
    return;
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      activeIndex.value = Math.min(
        activeIndex.value + 1,
        suggestions.value.length - 1
      );
      break;
    case 'ArrowUp':
      event.preventDefault();
      activeIndex.value = Math.max(activeIndex.value - 1, -1);
      break;
    case 'Enter':
      event.preventDefault();
      if (activeIndex.value >= 0 && suggestions.value[activeIndex.value]) {
        onSelect(suggestions.value[activeIndex.value] as Suggestion);
      } else {
        clear();
        emit('submit');
      }
      break;
    case 'Escape':
      clear();
      break;
  }
}

function onSelect(item: Suggestion) {
  emit('update:modelValue', item.value);
  clear();
  isFocused.value = false;
  // Submit the search with the selected keyword.
  nextTick(() => {
    emit('submit');
  });
}
</script>
